import { api } from './axios';

const MODERATION_BASE = '/moderation';

const validateId = (id) => {
    if (!id) throw new Error('ID is required');
};

/**
 * Типы модераторских карточек
 */
export const MODERATION_CARD_TYPES = {
    PINK_CARD: {
        label: 'Розовая карточка',
        color: 'pink',
        description: 'Предупреждение за первое некритичное нарушение (оффтоп, флуд). Не влияет на доступ.',
        permanent: false,
    },
    YELLOW_CARD: {
        label: 'Жёлтая карточка',
        color: 'yellow',
        description: 'Мут на форуме 24–72 часа. За разжигание споров, провокации, плагиат.',
        permanent: false,
        minDays: 1,
        maxDays: 3,
    },
    RED_CARD: {
        label: 'Красная карточка',
        color: 'red',
        description: 'Временная блокировка профиля 7–30 дней. За рецидив, клевету, чужие работы.',
        permanent: false,
        minDays: 7,
        maxDays: 30,
    },
    BLACKLIST: {
        label: 'Чёрный список',
        color: 'black',
        description: 'Полное удаление профиля + блокировка IP/HWID. За доксинг, угрозы, клонов.',
        permanent: true,
    },
    CONTENT_REMOVAL: {
        label: 'Удаление контента',
        color: 'none',
        description: 'Удаление контента без карточки.',
        permanent: false,
    },
};

/**
 * Статусы модераторских действий
 */
export const MODERATION_ACTION_STATUSES = {
    ACTIVE: 'Действует',
    EXPIRED: 'Срок истёк',
    REVOKED: 'Отозвана',
};

/**
 * Типы контентных сущностей для CONTENT_REMOVAL
 */
export const CONTENT_ENTITY_TYPES = {
    POST: 'POST',
    COMMENT: 'COMMENT',
    MESSAGE: 'MESSAGE',
    PROFILE: 'PROFILE',
    DUEL_WORK: 'DUEL_WORK',
};

export const moderationApi = {
    /**
     * Применить карточку к пользователю
     * @param {Object} payload — { targetUserId, cardType, reason, platformRuleId?, durationDays?, ipAddress?, hardwareId?, violations?, contentEntityType?, contentEntityId? }
     * @returns {Promise<Object>} ModerationActionResponse
     */
    applyCard: async (payload) => {
        if (!payload) throw new Error('Payload is required');
        try {
            const { data } = await api.post(`${MODERATION_BASE}/actions`, payload);
            return data;
        } catch (error) {
            console.error('[moderationApi.applyCard] Failed:', error.message);
            throw error;
        }
    },

    /**
     * Список всех модераторских действий
     * @param {Object} params — { status, cardType, page, size, sortBy, direction }
     * @returns {Promise<Object>} Page<ModerationActionResponse>
     */
    listActions: async (params = {}) => {
        try {
            const { data } = await api.get(`${MODERATION_BASE}/actions`, { params });
            return data;
        } catch (error) {
            console.error('[moderationApi.listActions] Failed:', error.message);
            throw error;
        }
    },

    /**
     * Получить действие по ID
     * @param {string} id — UUID действия
     * @returns {Promise<Object>} ModerationActionResponse
     */
    getAction: async (id) => {
        validateId(id);
        try {
            const { data } = await api.get(`${MODERATION_BASE}/actions/${id}`);
            return data;
        } catch (error) {
            console.error(`[moderationApi.getAction] Failed for ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * История действий пользователя
     * @param {string} userId — UUID пользователя
     * @param {Object} params — { status, cardType, page, size, sortBy, direction }
     * @returns {Promise<Object>} Page<ModerationActionResponse>
     */
    getUserActions: async (userId, params = {}) => {
        validateId(userId);
        try {
            const { data } = await api.get(`${MODERATION_BASE}/users/${userId}/actions`, { params });
            return data;
        } catch (error) {
            console.error(`[moderationApi.getUserActions] Failed for ${userId}:`, error.message);
            throw error;
        }
    },

    /**
     * Мои действия (история модератора)
     * @param {Object} params — { page, size, sortBy, direction }
     * @returns {Promise<Object>} Page<ModerationActionResponse>
     */
    getMyActions: async (params = {}) => {
        try {
            const { data } = await api.get(`${MODERATION_BASE}/actions/my`, { params });
            return data;
        } catch (error) {
            console.error('[moderationApi.getMyActions] Failed:', error.message);
            throw error;
        }
    },

    /**
     * Отозвать санкцию
     * @param {string} id — UUID действия
     * @param {Object} payload — { reason }
     * @returns {Promise<Object>} ModerationActionResponse
     */
    revoke: async (id, payload) => {
        validateId(id);
        if (!payload?.reason) throw new Error('Revoke reason is required');
        try {
            const { data } = await api.patch(`${MODERATION_BASE}/actions/${id}/revoke`, payload);
            return data;
        } catch (error) {
            console.error(`[moderationApi.revoke] Failed for ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Удвоить срок блокировки (только ADMIN)
     * @param {string} id — UUID действия
     * @returns {Promise<Object>} ModerationActionResponse
     */
    doubleDuration: async (id) => {
        validateId(id);
        try {
            const { data } = await api.patch(`${MODERATION_BASE}/actions/${id}/double-duration`);
            return data;
        } catch (error) {
            console.error(`[moderationApi.doubleDuration] Failed for ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Проверить мут пользователя
     * @param {string} userId — UUID пользователя
     * @returns {Promise<Object>} { muted: boolean }
     */
    checkMuted: async (userId) => {
        validateId(userId);
        try {
            const { data } = await api.get(`${MODERATION_BASE}/users/${userId}/muted`);
            return data;
        } catch (error) {
            console.error(`[moderationApi.checkMuted] Failed for ${userId}:`, error.message);
            throw error;
        }
    },

    /**
     * Проверить право на апелляцию
     * @param {string} id — UUID действия
     * @returns {Promise<Object>} { canAppeal: boolean }
     */
    canAppeal: async (id) => {
        validateId(id);
        try {
            const { data } = await api.get(`${MODERATION_BASE}/actions/${id}/can-appeal`);
            return data;
        } catch (error) {
            console.error(`[moderationApi.canAppeal] Failed for ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Проверить блокировку IP (только ADMIN)
     * @param {string} ipAddress — IP-адрес
     * @returns {Promise<Object>} { blocked: boolean }
     */
    checkIpBlock: async (ipAddress) => {
        if (!ipAddress) throw new Error('IP address is required');
        try {
            const { data } = await api.get(`${MODERATION_BASE}/ip-check`, { params: { ipAddress } });
            return data;
        } catch (error) {
            console.error('[moderationApi.checkIpBlock] Failed:', error.message);
            throw error;
        }
    },

    /**
     * Проверить блокировку HWID (только ADMIN)
     * @param {string} hardwareId — аппаратный ID
     * @returns {Promise<Object>} { blocked: boolean }
     */
    checkHwidBlock: async (hardwareId) => {
        if (!hardwareId) throw new Error('Hardware ID is required');
        try {
            const { data } = await api.get(`${MODERATION_BASE}/hwid-check`, { params: { hardwareId } });
            return data;
        } catch (error) {
            console.error('[moderationApi.checkHwidBlock] Failed:', error.message);
            throw error;
        }
    },

    /**
     * Санкция против организатора (только ADMIN)
     * @param {string} userId — UUID пользователя-организатора
     * @param {Object} payload — { reason }
     * @returns {Promise<Object>} OrganizerSanctionResponse
     */
    sanctionOrganizer: async (userId, payload) => {
        validateId(userId);
        if (!payload?.reason) throw new Error('Reason is required');
        try {
            const { data } = await api.post(`${MODERATION_BASE}/organizers/${userId}/sanction`, payload);
            return data;
        } catch (error) {
            console.error(`[moderationApi.sanctionOrganizer] Failed for ${userId}:`, error.message);
            throw error;
        }
    },
};
